/**
 * Events tab of the right panel (mockup `#rp-events-view` / `renderEventsTab`).
 *
 * The full chronological operational log for the active context — a superset of
 * the Alerts tab, because it also carries the `ok`/logged-only items the Alert
 * Router never promotes (FLYsafe MQTT Topic doc Appendix A routing table).
 * Plain rows, no severity-tinted cards, no acknowledge actions: those belong to
 * Alerts. Category chips are derived from whatever the context actually has.
 */
import { useAlertsStore } from '@/lib/store/alerts';
import { useDronesStore } from '@/lib/store/drones';
import { type Context, useUiStore } from '@/lib/store/ui';
import { cn } from '@/lib/utils';
import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { routeByContext } from './routing';

function timeAgo(iso: string) {
  const sec = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return 'now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

const DOT: Record<string, string> = {
  crit: 'bg-(--color-red)',
  warn: 'bg-(--color-amber)',
  info: 'bg-(--color-blue)',
  ok: 'bg-(--color-green)',
};

export function EventsPane() {
  const ctx = useUiStore((s) => s.activeContext);
  const selectedDroneId = useDronesStore((s) => s.selectedId);
  const items = useAlertsStore(useShallow((s) => s.items));
  const [cat, setCat] = useState<string>('all');

  const scoped = useMemo(
    () => items.filter((e) => routeByContext(e, ctx, selectedDroneId)),
    [items, ctx, selectedDroneId],
  );

  const cats = useMemo(() => {
    const seen: string[] = [];
    for (const e of scoped) if (!seen.includes(e.category)) seen.push(e.category);
    return ['all', ...seen];
  }, [scoped]);

  // A chip can disappear when the context changes; fall back rather than
  // silently rendering an empty list against a filter the user can't see.
  const activeCat = cats.includes(cat) ? cat : 'all';
  const visible = activeCat === 'all' ? scoped : scoped.filter((e) => e.category === activeCat);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {cats.length > 1 && (
        <div className="flex gap-1.5 flex-wrap px-3.5 pt-2 shrink-0">
          {cats.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={cn(
                'h-[22px] px-2.5 rounded-[11px] border text-[0.692em] font-semibold uppercase tracking-[0.4px] whitespace-nowrap transition-colors',
                c === activeCat
                  ? 'bg-(--color-selected) text-(--color-selected-fg) border-(--color-selected)'
                  : 'bg-(--color-bg) border-(--color-border) text-(--color-text3) hover:border-(--color-green) hover:text-(--color-green)',
              )}
            >
              {c === 'all' ? 'All' : c}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3.5 py-1.5">
        {visible.length === 0 && (
          <div className="text-[0.769em] text-(--color-text3) text-center py-6">
            {emptyLabel(ctx)}
          </div>
        )}
        {visible.map((e) => (
          <div
            key={e.event_id}
            className="grid grid-cols-[34px_8px_1fr] gap-1.5 py-[7px] border-b border-(--color-border-lite) last:border-b-0 items-start"
          >
            <span className="text-[0.692em] font-mono text-(--color-text3) text-right leading-[1.3]">
              {timeAgo(e.timestamp)}
            </span>
            <span
              className={cn(
                'w-[7px] h-[7px] rounded-full mt-1 shrink-0',
                DOT[e.severity] ?? DOT.info,
              )}
            />
            <div className="min-w-0">
              <span className="text-[0.615em] font-bold uppercase tracking-[0.4px] text-(--color-text3) mr-1.5">
                {e.category}
              </span>
              <span className="text-[0.769em] text-(--color-text2)">{e.message}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function emptyLabel(ctx: Context) {
  return ctx === 'inv' || ctx === 'apps' ? 'No events published for this context.' : 'No events.';
}
